import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SourceDrawer from '../../components/Assistant/SourceDrawer';
import { ChatDetailsPanel, InboxSessionList } from '../../features/sentiment';
import InboxFiltersSidebar from './components/InboxFiltersSidebar';
import AssistantChatPane from './components/AssistantChatPane';
import useAssistantInbox from './hooks/useAssistantInbox';
import useAssistantChat from './hooks/useAssistantChat';
import useSessionContext from '../../hooks/useSessionContext';

function ChatDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams();
  const { token, userId, workspaceId, refreshContext } = useSessionContext();

  const fetchSessionsListRef = useRef(async () => {});
  const selectedSessionIdRef = useRef(urlSessionId || null);
  const creatingNewChatRef = useRef(false);

  const updateURL = useCallback((sessionId) => {
    if (sessionId && sessionId !== 'new') {
      navigate(`/agent/chat/${sessionId}`, { replace: true });
    } else if (sessionId === 'new') {
      navigate('/agent/chat/new', { replace: true });
    } else {
      navigate('/agent/chat', { replace: true });
    }
  }, [navigate]);

  const chat = useAssistantChat({
    userId,
    activeSessionIdRef: selectedSessionIdRef,
    fetchSessionsListRef,
    refreshContext,
    workspaceId
  });

  const inbox = useAssistantInbox({
    urlSessionId,
    token,
    workspaceId,
    updateURL,
    loadSpecificSession: chat.loadSpecificSession,
    loadedSessionIdRef: chat.loadedSessionIdRef,
    fetchSessionsListRef,
    selectedSessionIdRef
  });

  const {
    chatSessions,
    selectedSessionId,
    setSelectedSessionId,
    loading,
    initialLoading,
    isInboxRefreshing,
    currentPage,
    totalPages,
    totalCount,
    itemsPerPage,
    hideSingleMessage,
    setHideSingleMessage,
    selectedFilters,
    setSelectedFilters,
    priorityCounts,
    visiblePriorityFilters,
    sortOptions,
    sortField,
    sortDirection,
    showSortDropdown,
    setShowSortDropdown,
    sortDropdownRef,
    handleSortChange,
    getCurrentSortOption,
    handlePageChange,
    handleRowClick,
    handleClearFilters,
    handleArchiveSession: archiveSession,
    bootstrappedWorkspaceRef
  } = inbox;

  const {
    messages,
    chatMetadata,
    isOwner,
    chatLoading,
    sessionSwitchLoading,
    isProcessing,
    isInitializing,
    input,
    textareaHeight,
    textAreaRef,
    messagesEndRef,
    messagesContainerRef,
    selectedSource,
    selectedCitation,
    drawerOpen,
    setDrawerOpen,
    handleCreateNewChat,
    resetSession,
    handleSourceClick,
    handleUserScroll,
    handleInputChange,
    handleKeyPress,
    handleSendMessage,
    handleStopGeneration
  } = chat;

  useEffect(() => {
    if (!bootstrappedWorkspaceRef.current || urlSessionId !== 'new' || creatingNewChatRef.current) return;

    creatingNewChatRef.current = true;
    handleCreateNewChat().finally(() => {
      creatingNewChatRef.current = false;
    });
  }, [urlSessionId, handleCreateNewChat, bootstrappedWorkspaceRef]);

  const handleArchiveSession = useCallback(async (sessionId) => {
    try {
      await archiveSession(sessionId);

      if (selectedSessionId === sessionId) {
        setSelectedSessionId(null);
        selectedSessionIdRef.current = null;
        resetSession();
      }
    } catch {
      alert('Failed to archive session');
    }
  }, [archiveSession, selectedSessionId, setSelectedSessionId, resetSession]);

  return (
    <div className="flex h-screen max-h-screen overflow-hidden bg-neutral-100">
      <InboxFiltersSidebar
        t={t}
        sortDropdownRef={sortDropdownRef}
        showSortDropdown={showSortDropdown}
        setShowSortDropdown={setShowSortDropdown}
        getCurrentSortOption={getCurrentSortOption}
        sortOptions={sortOptions}
        sortField={sortField}
        sortDirection={sortDirection}
        handleSortChange={handleSortChange}
        handleClearFilters={handleClearFilters}
        visiblePriorityFilters={visiblePriorityFilters}
        priorityCounts={priorityCounts}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        hideSingleMessage={hideSingleMessage}
        setHideSingleMessage={setHideSingleMessage}
      />

      <section className="flex flex-1 min-w-0 min-h-0 overflow-hidden bg-neutral-100 p-4">
        <div className="relative flex h-full min-h-0 w-full rounded-xl border border-neutral-200 bg-white overflow-hidden">
          <InboxSessionList
            t={t}
            workspaceId={workspaceId}
            chatSessions={chatSessions}
            selectedSessionId={selectedSessionId}
            loading={loading}
            initialLoading={initialLoading}
            isInboxRefreshing={isInboxRefreshing}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            itemsPerPage={itemsPerPage}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
          />

          <AssistantChatPane
            t={t}
            selectedSessionId={selectedSessionId}
            sessionSwitchLoading={sessionSwitchLoading}
            chatLoading={chatLoading}
            messages={messages}
            isProcessing={isProcessing}
            isOwner={isOwner}
            input={input}
            textareaHeight={textareaHeight}
            textAreaRef={textAreaRef}
            messagesEndRef={messagesEndRef}
            messagesContainerRef={messagesContainerRef}
            listLoading={loading}
            isInitializing={isInitializing}
            onUserScroll={handleUserScroll}
            onSourceClick={handleSourceClick}
            onInputChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onSendMessage={handleSendMessage}
            onStopGeneration={handleStopGeneration}
          />

          <ChatDetailsPanel
            sessionId={selectedSessionId}
            chatMetadata={chatMetadata}
            messages={messages}
            isOwner={isOwner}
            sessionsLoading={loading && !initialLoading}
            sessionLoading={chatLoading && !sessionSwitchLoading}
            onArchive={handleArchiveSession}
          />
        </div>
      </section>

      <SourceDrawer
        open={drawerOpen}
        setOpen={setDrawerOpen}
        source={selectedSource}
        citation={selectedCitation}
      />
    </div>
  );
}

export default ChatDashboard;
